import React, { useState, useEffect } from 'react';
import { AddressSuggestion, AddressValidationResult, clientService, ClientDTO } from '../../services/clientApi';

type ClientFormData = Omit<ClientDTO, 'id' | 'createdAt' | 'updatedAt'>;

interface ClientFormProps {
  client?: ClientDTO | null;
  onSubmit: (client: ClientFormData) => void;
  onCancel: () => void;
}

const getInitialFormData = (): ClientFormData => ({
  firstName: '',
  lastName: '',
  company: '',
  phone: '',
  email: '',
  address: '',
  postalCode: '',
  city: '',
  country: '',
  latitude: undefined,
  longitude: undefined,
  notes: '',
  tags: [],
  defaultVisitDuration: 60,
  isActive: true,
});

export const ClientForm: React.FC<ClientFormProps> = ({ client, onSubmit, onCancel }) => {
  const [formData, setFormData] = useState<ClientFormData>(getInitialFormData);

  const [tagInput, setTagInput] = useState('');
  const [isCheckingAddress, setIsCheckingAddress] = useState(false);
  const [addressCheckResult, setAddressCheckResult] = useState<AddressValidationResult | null>(null);
  const [addressCheckError, setAddressCheckError] = useState<string | null>(null);
  const [lastCheckedAddressKey, setLastCheckedAddressKey] = useState<string | null>(null);

  const buildAddressCheckKey = (data: Pick<ClientFormData, 'address' | 'postalCode' | 'city' | 'country'>): string => {
    return [data.address, data.postalCode, data.city, data.country]
      .map((value) => value?.trim().toLowerCase() || '')
      .join('|');
  };

  useEffect(() => {
    setAddressCheckResult(null);
    setAddressCheckError(null);
    setLastCheckedAddressKey(null);

    if (client) {
      setFormData({
        firstName: client.firstName,
        lastName: client.lastName,
        company: client.company || '',
        phone: client.phone || '',
        email: client.email || '',
        address: client.address,
        postalCode: client.postalCode || '',
        city: client.city || '',
        country: client.country || '',
        latitude: client.latitude,
        longitude: client.longitude,
        notes: client.notes || '',
        tags: client.tags || [],
        defaultVisitDuration: client.defaultVisitDuration || 60,
        isActive: client.isActive !== false,
      });
      return;
    }

    setFormData(getInitialFormData());
  }, [client]);

  const canCheckAddress = formData.address.trim().length > 0;

  const handleAddressFieldChange = (field: 'address' | 'postalCode' | 'city' | 'country', value: string) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
    setAddressCheckResult(null);
    setAddressCheckError(null);
    setLastCheckedAddressKey(null);
  };

  const runAddressCheck = async (force = false) => {
    if (!canCheckAddress) {
      return;
    }

    const currentKey = buildAddressCheckKey(formData);
    if (!force && currentKey === lastCheckedAddressKey) {
      return;
    }

    if (isCheckingAddress) {
      return;
    }

    setIsCheckingAddress(true);
    setAddressCheckError(null);

    try {
      const response = await clientService.validateAddress({
        address: formData.address,
        postalCode: formData.postalCode,
        city: formData.city,
        country: formData.country?.trim() || undefined,
      });

      if (response.success && response.data) {
        setAddressCheckResult(response.data);
        setLastCheckedAddressKey(currentKey);
      }
    } catch (error: any) {
      console.error('Erreur lors de la vérification d\'adresse:', error);
      setAddressCheckError(error.response?.data?.message || 'Impossible de vérifier l\'adresse pour le moment.');
    } finally {
      setIsCheckingAddress(false);
    }
  };

  const handleCheckAddress = async () => {
    await runAddressCheck(true);
  };

  const handleAddressBlur = async () => {
    await runAddressCheck(false);
  };

  const handleApplyAddressSuggestion = (suggestion: AddressSuggestion) => {
    setFormData((prev) => ({
      ...prev,
      address: suggestion.address,
      postalCode: suggestion.postalCode,
      city: suggestion.city,
      country: suggestion.country,
      latitude: suggestion.latitude,
      longitude: suggestion.longitude,
    }));

    setAddressCheckResult({
      isValid: true,
      confidence: suggestion.score,
      suggestions: [suggestion],
    });
    setAddressCheckError(null);
    setLastCheckedAddressKey(
      buildAddressCheckKey({
        address: suggestion.address,
        postalCode: suggestion.postalCode,
        city: suggestion.city,
        country: suggestion.country,
      })
    );
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSubmit({
      ...formData,
      country: formData.country?.trim() || undefined,
    });
  };

  const handleAddTag = () => {
    if (tagInput.trim() && !formData.tags?.includes(tagInput.trim())) {
      setFormData({
        ...formData,
        tags: [...(formData.tags || []), tagInput.trim()],
      });
      setTagInput('');
    }
  };

  const handleRemoveTag = (tag: string) => {
    setFormData({
      ...formData,
      tags: formData.tags?.filter((t) => t !== tag) || [],
    });
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Prénom */}
        <div>
          <label htmlFor="firstName" className="block text-sm font-medium text-gray-700 mb-1">
            Prénom <span className="text-red-500">*</span>
          </label>
          <input
            id="firstName"
            type="text"
            value={formData.firstName}
            onChange={(e) => setFormData({ ...formData, firstName: e.target.value })}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            required
          />
        </div>

        {/* Nom */}
        <div>
          <label htmlFor="lastName" className="block text-sm font-medium text-gray-700 mb-1">
            Nom <span className="text-red-500">*</span>
          </label>
          <input
            id="lastName"
            type="text"
            value={formData.lastName}
            onChange={(e) => setFormData({ ...formData, lastName: e.target.value })}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            required
          />
        </div>

        {/* Entreprise */}
        <div>
          <label htmlFor="company" className="block text-sm font-medium text-gray-700 mb-1">Entreprise</label>
          <input
            id="company"
            type="text"
            value={formData.company}
            onChange={(e) => setFormData({ ...formData, company: e.target.value })}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
        </div>

        {/* Téléphone */}
        <div>
          <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-1">Téléphone</label>
          <input
            id="phone"
            type="tel"
            value={formData.phone}
            onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
        </div>

        {/* Email */}
        <div className="md:col-span-2">
          <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">Email</label>
          <input
            id="email"
            type="email"
            value={formData.email}
            onChange={(e) => setFormData({ ...formData, email: e.target.value })}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
        </div>

        {/* Adresse */}
        <div className="md:col-span-2">
          <label htmlFor="address" className="block text-sm font-medium text-gray-700 mb-1">
            Adresse <span className="text-red-500">*</span>
          </label>
          <input
            id="address"
            type="text"
            value={formData.address}
            onChange={(e) => handleAddressFieldChange('address', e.target.value)}
            onBlur={handleAddressBlur}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            required
          />
        </div>

        {/* Code postal */}
        <div>
          <label htmlFor="postalCode" className="block text-sm font-medium text-gray-700 mb-1">Code postal</label>
          <input
            id="postalCode"
            type="text"
            value={formData.postalCode}
            onChange={(e) => handleAddressFieldChange('postalCode', e.target.value)}
            onBlur={handleAddressBlur}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
        </div>

        {/* Ville */}
        <div>
          <label htmlFor="city" className="block text-sm font-medium text-gray-700 mb-1">Ville</label>
          <input
            id="city"
            type="text"
            value={formData.city}
            onChange={(e) => handleAddressFieldChange('city', e.target.value)}
            onBlur={handleAddressBlur}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
        </div>

        <div className="md:col-span-2">
          <label htmlFor="country" className="block text-sm font-medium text-gray-700 mb-1">Pays</label>
          <input
            id="country"
            type="text"
            value={formData.country || ''}
            onChange={(e) => handleAddressFieldChange('country', e.target.value)}
            onBlur={handleAddressBlur}
            placeholder="Ex: France, United States, Germany"
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
        </div>

        <div className="md:col-span-2 rounded-lg border border-gray-200 bg-gray-50 p-3">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <p className="text-sm font-medium text-gray-700">Vérification d'adresse (non bloquante)</p>
            <button
              type="button"
              onClick={handleCheckAddress}
              disabled={!canCheckAddress || isCheckingAddress}
              className="px-3 py-1.5 text-sm rounded-lg border border-gray-300 bg-white hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isCheckingAddress ? 'Vérification...' : 'Vérifier l\'adresse'}
            </button>
          </div>

          {addressCheckError && (
            <p className="mt-2 text-sm text-red-600">{addressCheckError}</p>
          )}

          {addressCheckResult && (
            <div className="mt-3 space-y-2">
              {addressCheckResult.isValid ? (
                <div className="rounded-lg border border-green-200 bg-green-50 px-3 py-2 text-sm text-green-700">
                  Adresse cohérente détectée (confiance {(addressCheckResult.confidence * 100).toFixed(0)}%).
                </div>
              ) : (
                <div className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800">
                  {addressCheckResult.warning || 'Adresse possiblement inexacte.'}
                </div>
              )}

              {addressCheckResult.suggestions.length > 0 && (
                <div className="space-y-2">
                  <p className="text-xs text-gray-500">Suggestions:</p>
                  {addressCheckResult.suggestions.map((suggestion) => (
                    <div
                      key={`${suggestion.label}-${suggestion.postalCode}-${suggestion.city}`}
                      className="flex flex-wrap items-center justify-between gap-2 rounded-lg border border-gray-200 bg-white px-3 py-2"
                    >
                      <div className="min-w-0">
                        <p className="text-sm font-medium text-gray-800 truncate">{suggestion.label}</p>
                        <p className="text-xs text-gray-500">Confiance {(suggestion.score * 100).toFixed(0)}%</p>
                      </div>
                      <button
                        type="button"
                        onClick={() => handleApplyAddressSuggestion(suggestion)}
                        className="px-3 py-1.5 text-xs rounded-lg bg-blue-600 text-white hover:bg-blue-700"
                      >
                        Appliquer
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Durée de visite par défaut */}
        <div>
          <label htmlFor="defaultVisitDuration" className="block text-sm font-medium text-gray-700 mb-1">
            Durée de visite (minutes)
          </label>
          <input
            id="defaultVisitDuration"
            type="number"
            min="15"
            step="15"
            value={formData.defaultVisitDuration}
            onChange={(e) => setFormData({ ...formData, defaultVisitDuration: Number.parseInt(e.target.value, 10) })}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
        </div>

        {/* Statut actif */}
        <div className="flex items-center">
          <input
            type="checkbox"
            id="isActive"
            checked={formData.isActive}
            onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
            className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
          />
          <label htmlFor="isActive" className="ml-2 block text-sm text-gray-900">
            Client actif
          </label>
        </div>
      </div>

      {/* Tags */}
      <div>
        <label htmlFor="tagsInput" className="block text-sm font-medium text-gray-700 mb-1">Tags</label>
        <div className="flex gap-2 mb-2">
          <input
            id="tagsInput"
            type="text"
            value={tagInput}
            onChange={(e) => setTagInput(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); handleAddTag(); } }}
            placeholder="Ajouter un tag..."
            className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
          <button
            type="button"
            onClick={handleAddTag}
            className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300"
          >
            Ajouter
          </button>
        </div>
        <div className="flex flex-wrap gap-2">
          {formData.tags?.map((tag) => (
            <span
              key={tag}
              className="inline-flex items-center px-3 py-1 rounded-full text-sm bg-blue-100 text-blue-800"
            >
              {tag}
              <button
                type="button"
                onClick={() => handleRemoveTag(tag)}
                className="ml-2 text-blue-600 hover:text-blue-800"
              >
                ×
              </button>
            </span>
          ))}
        </div>
      </div>

      {/* Notes */}
      <div>
        <label htmlFor="notes" className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
        <textarea
          id="notes"
          value={formData.notes}
          onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
          rows={3}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
        />
      </div>

      {/* Boutons */}
      <div className="flex justify-end gap-3 pt-4">
        <button
          type="button"
          onClick={onCancel}
          className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
        >
          Annuler
        </button>
        <button
          type="submit"
          className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
        >
          {client ? 'Modifier' : 'Créer'}
        </button>
      </div>
    </form>
  );
};
